import React from "react";
import { useSearchParams } from "react-router-dom";
import { MdArrowBack, MdAttachFile, MdCheckCircle, MdSend, MdSupportAgent } from "react-icons/md";
import api from "lib/axios";
import { useAppLanguage } from "hooks/useAppLanguage";
import logoLight from "assets/img/layout/easyfollow-logo.png";
import { getSupportContactSettings, type SupportContactSettings } from "services/supportContactSettingsService";

type FormState = {
  name: string;
  phone: string;
  problem: string;
};

export default function ContactSupportPage() {
  const isArabic = useAppLanguage() === "ar";
  const [searchParams] = useSearchParams();
  const [form, setForm] = React.useState<FormState>({ name: "", phone: "", problem: "" });
  const [files, setFiles] = React.useState<File[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const [settings, setSettings] = React.useState<SupportContactSettings | null>(null);

  React.useEffect(() => {
    let active = true;
    void getSupportContactSettings()
      .then((data) => {
        if (active) setSettings(data);
      })
      .catch(() => {
        // The built-in copy remains the fallback when the API is unavailable.
      });
    return () => {
      active = false;
    };
  }, []);

  const pageTitle = isArabic
    ? settings?.button_label_ar || "هل واجهت مشكلة؟ تواصل معنا"
    : settings?.button_label_en || "Having a problem? Contact us";
  const pageDescription = isArabic
    ? settings?.button_subtitle_ar || "أرسل تفاصيل المشكلة إلى الإدارة مع المرفقات"
    : settings?.button_subtitle_en || "Send the problem details and attachments to admin";
  const copy = (arabic: keyof SupportContactSettings, english: keyof SupportContactSettings, fallbackArabic: string, fallbackEnglish: string) =>
    isArabic ? settings?.[arabic] || fallbackArabic : settings?.[english] || fallbackEnglish;
  const nameLabel = copy("name_label_ar", "name_label_en", "الاسم", "Name");
  const namePlaceholder = copy("name_placeholder_ar", "name_placeholder_en", "اكتب اسمك", "Your name");
  const phoneLabel = copy("phone_label_ar", "phone_label_en", "رقم الهاتف", "Phone number");
  const phonePlaceholder = copy("phone_placeholder_ar", "phone_placeholder_en", "رقم يمكن التواصل عليه", "A number we can reach");
  const problemLabel = copy("problem_label_ar", "problem_label_en", "المشكلة", "Problem");
  const problemPlaceholder = copy("problem_placeholder_ar", "problem_placeholder_en", "اشرح المشكلة بالتفصيل", "Describe the problem");
  const attachmentsLabel = copy("attachments_label_ar", "attachments_label_en", "مرفقات (اختياري، حتى 3 ملفات)", "Attachments (optional, up to 3 files)");
  const attachmentsButton = copy("attachments_button_ar", "attachments_button_en", "اختيار ملفات", "Choose files");
  const submitLabel = copy("submit_label_ar", "submit_label_en", "إرسال للإدارة", "Send to admin");
  const sendingLabel = copy("sending_label_ar", "sending_label_en", "جارٍ الإرسال…", "Sending…");

  const updateField = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError("");
  };

  const onFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).slice(0, 3);
    setFiles(selected);
    setError("");
  };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSaving(true);

    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("phone", form.phone.trim());
    payload.append("problem", form.problem.trim());
    payload.append("source", searchParams.get("source") || "ios_wallet");
    files.forEach((file) => payload.append("attachments[]", file, file.name));

    try {
      await api.post("/support/requests", payload, {
        headers: { "Content-Type": "multipart/form-data" },
        meta: { toast: false },
      });
      setSent(true);
    } catch (requestError: any) {
      const responseErrors = requestError?.response?.data?.errors;
      const firstError = responseErrors && typeof responseErrors === "object"
        ? Object.values(responseErrors).flat().find((value) => typeof value === "string")
        : null;
      setError(
        String(firstError || requestError?.response?.data?.message || (isArabic
          ? "تعذر إرسال الطلب. راجع البيانات وحاول مرة أخرى."
          : "Could not send your request. Check the details and try again."))
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <main dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-[#070a10] px-4 py-8 text-white sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-8 flex items-center justify-between gap-4">
          <img src={logoLight} alt="Easyfollow Platform" className="h-8 w-auto opacity-90" />
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-3 py-2 text-sm font-semibold text-white/75 transition hover:bg-white/10 hover:text-white"
          >
            {isArabic ? "رجوع" : "Back"}
            <MdArrowBack className={isArabic ? "rotate-180" : ""} />
          </a>
        </header>

        <section className="overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#15213a] to-[#0d1424] shadow-2xl shadow-black/30">
          <div className="border-b border-white/10 bg-gradient-to-r from-brand-600/25 via-indigo-600/15 to-transparent p-6 sm:p-8">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/20 text-brand-300">
              <MdSupportAgent className="h-8 w-8" />
            </div>
            <h1 className="text-2xl font-black tracking-tight sm:text-3xl">
              {pageTitle}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-7 text-slate-300 sm:text-base">
              {pageDescription}
            </p>
          </div>

          {sent ? (
            <div className="flex flex-col items-center px-6 py-16 text-center sm:px-10">
              <MdCheckCircle className="h-16 w-16 text-emerald-400" />
              <h2 className="mt-5 text-xl font-bold">{isArabic ? "تم إرسال طلبك" : "Your request was sent"}</h2>
              <p className="mt-3 max-w-md text-sm leading-7 text-slate-300">
                {isArabic
                  ? "وصل الطلب إلى الإدارة، وسنتواصل معك على رقم الهاتف المدخل."
                  : "The request reached the admin team. We will contact you using the phone number you provided."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", phone: "", problem: "" });
                  setFiles([]);
                }}
                className="mt-7 rounded-xl bg-brand-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-brand-600"
              >
                {isArabic ? "إرسال طلب آخر" : "Send another request"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 p-6 sm:p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block text-sm font-semibold text-slate-200">
                  {nameLabel}
                  <input
                    required
                    minLength={2}
                    maxLength={120}
                    value={form.name}
                    onChange={updateField("name")}
                    className="ef-input-field mt-2"
                    placeholder={namePlaceholder}
                  />
                </label>
                <label className="block text-sm font-semibold text-slate-200">
                  {phoneLabel}
                  <input
                    required
                    minLength={6}
                    maxLength={32}
                    value={form.phone}
                    onChange={updateField("phone")}
                    className="ef-input-field mt-2"
                    placeholder={phonePlaceholder}
                    inputMode="tel"
                  />
                </label>
              </div>

              <label className="block text-sm font-semibold text-slate-200">
                {problemLabel}
                <textarea
                  required
                  minLength={5}
                  maxLength={5000}
                  rows={6}
                  value={form.problem}
                  onChange={updateField("problem")}
                  className="ef-input-field mt-2 resize-y"
                  placeholder={problemPlaceholder}
                />
              </label>

              <label className="block text-sm font-semibold text-slate-200">
                <span>{attachmentsLabel}</span>
                <span className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-white/[0.04] px-4 py-4 text-sm font-medium text-slate-300 transition hover:border-brand-400/70 hover:bg-white/[0.07]">
                  <MdAttachFile className="h-5 w-5" />
                  {attachmentsButton}
                  <input type="file" multiple onChange={onFilesChange} className="sr-only" accept="image/*,.pdf,.doc,.docx" />
                </span>
                {files.length > 0 ? (
                  <span className="mt-2 block text-xs font-normal text-slate-400">
                    {files.map((file) => file.name).join("، ")}
                  </span>
                ) : null}
              </label>

              {error ? <p className="rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200" role="alert">{error}</p> : null}

              <button
                type="submit"
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-brand-600 disabled:cursor-wait disabled:opacity-60"
              >
                <MdSend className={isArabic ? "rotate-180" : ""} />
                {saving ? sendingLabel : submitLabel}
              </button>
            </form>
          )}
        </section>

        <p className="mt-6 text-center text-xs text-slate-500">
          {isArabic ? "لن نستخدم بياناتك إلا لمتابعة طلب الدعم." : "Your details are used only to follow up on this support request."}
        </p>
      </div>
    </main>
  );
}
